import { FlagOutlined, HistoryOutlined } from '@ant-design/icons';
import {
  describeError,
  FINANCE_ERRORS,
  fullDate,
  present,
} from '@horof/shared';
import { Alert, App, Button, Collapse, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { financeContext, useLists } from './data';
import { actionLabel, changeLines } from './history';
import {
  FlagForReviewDocument,
  type ReviewKind,
  TrailDocument,
} from './review-data';

/** «12 مارس 2026 · 14:05», by whom when arqam knows. */
const when = (at: string, by: string | null) =>
  `${fullDate(at)} · ${dayjs(at).format('HH:mm')}${by ? ` · ${by}` : ''}`;

/**
 * Under a row's form: who asked for a review and who cleared it, and its
 * history of changes (arqam doc 54), most recent first.
 */
export function ReviewTrail({ kind, id }: { kind: ReviewKind; id: string }) {
  const [{ data }] = useQuery({
    query: TrailDocument,
    variables: {
      id,
      entity: kind,
      operation: kind === 'operation',
      transfer: kind === 'transfer',
      inKind: kind === 'in_kind',
    },
    context: useMemo(() => financeContext, []),
  });
  const [{ data: lists }] = useLists();
  const names = useMemo(
    () =>
      new Map(
        [
          ...present(lists?.projects?.nodes),
          ...present(lists?.financialAccounts?.nodes),
          ...present(lists?.operationCategories?.nodes),
          ...present(lists?.counterparties?.nodes),
        ].map((x) => [x.id, x.name]),
      ),
    [lists],
  );
  const row =
    data?.financialOperation ??
    data?.accountTransfer ??
    data?.inKindContribution;
  const changes = present(data?.financeChanges?.nodes);
  if (!data) return null;

  return (
    <div className="desk-trail">
      {row?.reviewRequestedAt && (
        <div className="field-hint">
          طُلبت المراجعة: {when(row.reviewRequestedAt, row.reviewRequestedBy)}
        </div>
      )}
      {row?.reviewedAt && !row.needsReview && (
        <div className="field-hint">
          رُوجعت: {when(row.reviewedAt, row.reviewedBy)}
        </div>
      )}
      {changes.length > 0 && (
        <Collapse
          size="small"
          ghost
          items={[
            {
              key: 'history',
              label: (
                <span>
                  <HistoryOutlined /> سجل التعديلات (
                  <span className="ltr-num">
                    {data.financeChanges?.totalCount}
                  </span>
                  )
                </span>
              ),
              children: (
                <ol className="desk-history">
                  {changes.map((c) => {
                    const lines =
                      c.action === 'update'
                        ? changeLines(c.changes, names)
                        : [];
                    return (
                      <li key={c.id}>
                        <div className="field-hint">
                          {actionLabel(c.action)}:{' '}
                          {when(c.changedAt, c.changedBy)}
                        </div>
                        {lines.map((l) => (
                          <div key={l.field} className="desk-history-line">
                            <span>{l.field}:</span>{' '}
                            <span className="is-before">{l.before}</span>
                            {' ← '}
                            <span className="is-after">{l.after}</span>
                          </div>
                        ))}
                      </li>
                    );
                  })}
                </ol>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

/**
 * «طلب مراجعة» for those who read the finances without editing them (the
 * committee): a note on what to check. arqam adds it to the existing note.
 */
export function FlagButton({ kind, id }: { kind: ReviewKind; id: string }) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching }, flag] = useMutation(FlagForReviewDocument);

  async function send() {
    if (!note.trim()) return setProblem('اكتب ما يجب التحقق منه.');
    const r = await flag({ kind, id, note: note.trim() }, financeContext);
    if (r.error) {
      setProblem(
        describeError(r.error, 'تعذّر طلب المراجعة.', {
          ...FINANCE_ERRORS,
          REVIEW_NOTE_REQUIRED: 'اكتب ما يجب التحقق منه.',
          REVIEW_TARGET_NOT_FOUND: 'لم تعد موجودة. حدّث الصفحة.',
        }),
      );
      return;
    }
    message.success('أُرسل طلب المراجعة إلى أمين المال');
    setOpen(false);
    setNote('');
  }

  return (
    <>
      <Button icon={<FlagOutlined />} onClick={() => setOpen(true)}>
        طلب مراجعة
      </Button>
      <Modal
        open={open}
        title="طلب مراجعة"
        okText="إرسال الطلب"
        cancelText="رجوع"
        confirmLoading={fetching}
        onOk={send}
        onCancel={() => setOpen(false)}
      >
        <p className="field-hint">
          يظهر الطلب لأمين المال في «المراجعة» مع ملاحظتك.
        </p>
        <Input.TextArea
          autoFocus
          autoSize={{ minRows: 2, maxRows: 5 }}
          maxLength={500}
          placeholder="ما الذي يجب التحقق منه؟"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setProblem(null);
          }}
        />
        {problem && <Alert type="error" showIcon message={problem} />}
      </Modal>
    </>
  );
}
